import type { jsPDF } from 'jspdf';
import { GState } from 'jspdf';

const LOGO_URL = '/focuzon.png';
const WATERMARK_OPACITY = 0.2;
const WATERMARK_SIZE_MM = 12;
/** Footer line is at pageHeight-18 (invoice) or pageHeight-15 (others). Logo sits 2mm below it. */
const GAP_BELOW_FOOTER_LINE = 2;

let logoBase64Cache: string | null = null;

/**
 * Fetches the product logo and returns it as a base64 data URL.
 * Caches the result for subsequent calls.
 */
export async function getLogoBase64(): Promise<string> {
    if (logoBase64Cache) return logoBase64Cache;

    const res = await fetch(LOGO_URL);
    if (!res.ok) throw new Error('Failed to load logo for watermark');

    const blob = await res.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });

    logoBase64Cache = base64;
    return base64;
}

/**
 * Adds a small product logo watermark in the footer corner of every page.
 * The logo appears in the bottom-left corner with low opacity.
 */
export async function addWatermarkToPdf(doc: jsPDF): Promise<void> {
    const logoBase64 = await getLogoBase64();
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const w = WATERMARK_SIZE_MM;
    const h = WATERMARK_SIZE_MM;
    const margin = 14;
    const x = margin;
    /** Position logo just below the footer line (use 15 as lowest line; +2mm gap) */
    const y = pageHeight - 15 + GAP_BELOW_FOOTER_LINE;

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.saveGraphicsState();
        doc.setGState(new GState({ opacity: WATERMARK_OPACITY }));
        doc.addImage(logoBase64, 'PNG', x, y, w, h);
        doc.restoreGraphicsState();
    }
}
